import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import { StarRating } from '../components/StarRating';
import styles from './CourseFeedbackPage.module.css';

interface FeedbackReview {
  id: string;
  rating: number;
  feedback: string;
  created_at: string;
  author_name: string;
}

interface RatingBucket {
  rating: number;
  count: number;
  percentage: number;
}

interface CourseFeedbackDetail {
  course_id: string;
  title: string;
  average_rating: number | null;
  rating_count: number;
  distribution: RatingBucket[];
  reviews: FeedbackReview[];
}

export function CourseFeedbackPage(): JSX.Element {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CourseFeedbackDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);

  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        setIsLoading(true);
        const res = await apiClient.get<{ data: CourseFeedbackDetail }>(`/courses/${courseId}/feedback`);
        if (!cancelled) {
          setDetail(res.data.data);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const filteredReviews = useMemo(() => {
    if (!detail) return [];
    if (selectedRating == null) return detail.reviews;
    return detail.reviews.filter((review) => review.rating === selectedRating);
  }, [detail, selectedRating]);

  if (isLoading) {
    return <main className={styles.page}><p className={styles.stateText}>Loading reviews…</p></main>;
  }

  if (!detail) {
    return (
      <main className={styles.page}>
        <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <p className={styles.stateText}>Course feedback is not available right now.</p>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>
        ← Back
      </button>

      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Learner Reviews</p>
          <h1 className={styles.title}>{detail.title}</h1>
        </div>
      </header>

      <section className={styles.summaryCard}>
        <div className={styles.summaryLeft}>
          <div className={styles.averageValue}>{(detail.average_rating ?? 0).toFixed(2)}</div>
          <div className={styles.averageStars}>
            <StarRating
              value={detail.average_rating ?? 0}
              size="lg"
              ariaLabel={`${detail.average_rating ?? 0} out of 5`}
            />
            <span className={styles.averageLabel}>{(detail.average_rating ?? 0).toFixed(2)}/5</span>
          </div>
          <p className={styles.reviewCount}>{detail.rating_count} ratings</p>
        </div>

        <div className={styles.summaryRight}>
          {detail.distribution.map((bucket) => {
            const active = selectedRating === bucket.rating;
            return (
              <button
                key={bucket.rating}
                type="button"
                className={`${styles.bucketRow} ${active ? styles.bucketRowActive : ''}`}
                onClick={() => setSelectedRating((prev) => (prev === bucket.rating ? null : bucket.rating))}
              >
                <span className={styles.bucketLabel}>{bucket.rating} star</span>
                <span className={styles.bucketBar}>
                  <span className={styles.bucketFill} style={{ width: `${bucket.percentage}%` }} />
                </span>
                <span className={styles.bucketPercent}>{Math.round(bucket.percentage)}%</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className={styles.reviewsCard}>
        <div className={styles.reviewsHeader}>
          <h2 className={styles.sectionTitle}>
            {selectedRating == null ? 'All feedback' : `${selectedRating}-star feedback`}
          </h2>
          {selectedRating != null && (
            <button type="button" className={styles.clearFilter} onClick={() => setSelectedRating(null)}>
              Clear filter
            </button>
          )}
        </div>

        {filteredReviews.length === 0 ? (
          <p className={styles.emptyText}>No learner feedback matches this filter yet.</p>
        ) : (
          <div className={styles.reviewList}>
            {filteredReviews.map((review) => (
              <article key={review.id} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div>
                    <h3 className={styles.authorName}>{review.author_name}</h3>
                    <p className={styles.reviewDate}>
                      {new Date(review.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className={styles.reviewStars}>
                    <StarRating value={review.rating} ariaLabel={`${review.rating} out of 5`} />
                    <span className={styles.reviewRatingText}>{review.rating}/5</span>
                  </div>
                </div>
                <p className={styles.reviewText}>{review.feedback || 'No written feedback provided.'}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
